import { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";

const fetchReportsTypeList = (id) =>
  axiosInstance.get(`/reports/list-all?reportType=${id}`).then((r) => r.data);

const fetchRolesDropdown = () =>
  axiosInstance.get(`/roles`).then((r) => r.data);

const fetchTeamDropdown = () =>
  axiosInstance.get(`/team/dropdown`).then((r) => r.data);

const fetchUserRegionResource = (userId) => {
  const p = new URLSearchParams();
  if (userId) p.append("user_id", userId);
  return axiosInstance
    .get(`/users/user-region-resources?${p.toString()}`)
    .then((r) => r.data);
};

const fetchUserResource = ({ region_id, resource_type }) => {
  const p = new URLSearchParams();
  if (region_id?.length && resource_type?.length) {
    p.append("region_id", region_id.join(","));
    p.append("resource_type", resource_type.join(","));
  }
  return axiosInstance
    .get(`/users/resources?${p.toString()}`)
    .then((r) => r.data);
};

function findRegionsByIds(data, ids) {
  const res = [];
  function walk(n) {
    if (ids.includes(Number(n.codeValue)))
      res.push({ id: n.codeValue, name: n.label });
    n.subClass?.forEach(walk);
  }
  data?.forEach(walk);
  return res;
}

function getResourceCodeFromFilters(regionResourceData, resourceTypeLabel) {
  return regionResourceData?.filters
    ?.find((f) => f?.filterName === "resource_type")
    ?.filterData?.find((f) => resourceTypeLabel === f?.label)?.codeValue;
}

function createUserAccessObject(
  resourceOptions,
  userAfter,
  regionResourceData
) {
  if (!resourceOptions?.length || !userAfter?.resourceType) return [];
  return (
    resourceOptions
      ?.filter((opt) =>
        userAfter.resourceType.includes(
          Number(
            getResourceCodeFromFilters(regionResourceData, opt.resourceType)
          )
        )
      )
      .map((opt) => ({
        ...opt,
        resourceCode: getResourceCodeFromFilters(
          regionResourceData,
          opt.resourceType
        ),
        resourceData: opt.resourceData?.filter((f) =>
          userAfter.reconAccountIds?.includes(f?.id)
        ),
      })) ?? []
  );
}

function getCommonIds(a1 = null, a2 = null) {
  a1 = a1 ?? [];
  a2 = a2 ?? [];
  return Array.from(new Set([...a1, ...a2]));
}

export const useUserConversion = (userAfter, userBefore = null) => {
  const userId = userBefore?.userId;

  const regionIds = getCommonIds(userBefore?.regionIds, userAfter?.regionIds);
  const resourceTypeIds = getCommonIds(
    userBefore?.userAccess?.map((ua) => ua?.resourceCode),
    userAfter?.resourceType
  );

  const [teamsData, setTeamsData] = useState();
  const [rolesData, setRolesData] = useState();
  const [systemReportsData, setSystemReportsData] = useState();
  const [userReportsData, setUserReportsData] = useState();
  const [regionResourceData, setRegionResourceData] = useState();
  const [resourcesData, setResourcesData] = useState();

  useEffect(() => {
    fetchTeamDropdown().then(setTeamsData);
    fetchRolesDropdown().then(setRolesData);
    fetchReportsTypeList(1).then(setSystemReportsData);
    fetchReportsTypeList(2).then(setUserReportsData);
    fetchUserRegionResource(userId ?? null).then(setRegionResourceData);
  }, [userId]);

  useEffect(() => {
    if (regionIds.length && resourceTypeIds.length)
      fetchUserResource({
        region_id: regionIds.map(String),
        resource_type: resourceTypeIds.map(String),
      }).then(setResourcesData);
  }, [regionIds.join(","), resourceTypeIds.join(",")]);

  const regionResourceOptions =
    regionResourceData?.filters?.find((f) => f?.filterName === "region") || [];

  const structuredUserBefore = userBefore
    ? {
        ...userBefore,
        teamId: teamsData?.find((o) => o?.id === userBefore.teamId) ?? null,
        roles:
          rolesData?.roles
            ?.filter((o) =>
              (userBefore.roles || []).map((r) => r.id).includes(o?.id)
            )
            .map((o) => ({ id: o.id, name: o.name })) ?? [],
        regionIds:
          findRegionsByIds(
            regionResourceOptions.filterData,
            userBefore.regionIds || []
          ) ?? [],
        userReportAccess:
          userReportsData
            ?.filter((o) => (userBefore.reportAccess || []).includes(o?.id))
            .map((o) => ({ id: o.id, name: o.reportName })) ?? [],
        systemReportAccess:
          systemReportsData
            ?.filter((o) => (userBefore.reportAccess || []).includes(o?.id))
            .map((o) => ({ id: o.id, name: o.reportName })) ?? [],
      }
    : {};

  const structuredUserAfter = userAfter
    ? {
        ...userAfter,
        teamId: teamsData?.find((o) => o?.id === userAfter.teamId) ?? null,
        roles:
          rolesData?.roles
            ?.filter((o) => userAfter.roles?.includes(o?.id))
            .map((o) => ({ id: o.id, name: o.name })) ?? [],
        regionIds:
          findRegionsByIds(
            regionResourceOptions.filterData,
            userAfter.regionIds || []
          ) ?? [],
        userReportAccess:
          userReportsData
            ?.filter((o) => userAfter.reportAccess?.includes(o?.id))
            .map((o) => ({ id: o.id, name: o.reportName })) ?? [],
        systemReportAccess:
          systemReportsData
            ?.filter((o) => userAfter.reportAccess?.includes(o?.id))
            .map((o) => ({ id: o.id, name: o.reportName })) ?? [],
        userAccess: createUserAccessObject(
          resourcesData,
          userAfter,
          regionResourceData
        ),
      }
    : {};

  return { structuredUserBefore, structuredUserAfter };
};

export const ProcessUserCreationData = async (workQueueData) => {
  const user = workQueueData?.user?.userEntity?.restrictedUser
    ? workQueueData?.user
    : {
        ...workQueueData?.user,
        reconAccountIds: [],
        regionIds: [],
        resourceType: [],
        userEntity: {
          ...workQueueData?.user?.userEntity,
          reconAccountIds: [],
          regionIds: [],
          resourceType: [],
        },
      };

  const [
    systemReportsData,
    userReportsData,
    regionResourceData,
    resourcesData,
  ] = await Promise.all([
    fetchReportsTypeList(1),
    fetchReportsTypeList(2),
    fetchUserRegionResource(null),
    user?.regions?.length && user?.resourceType?.length
      ? fetchUserResource({
          region_id: user.regions.map((r) => r.id),
          resource_type: user.resourceType.map((rt) => rt.id),
        })
      : Promise.resolve({ data: [] }),
  ]);

  const reportIds = user?.reportAccess?.map((ra) => ra.id) || [];
  const resourceTypeIds = user?.resourceType?.map((rt) => rt.id) || [];
  const reconAccountIds = user?.reconAccounts?.map((ra) => ra.id) || [];

  return {
    ...user,
    systemReports:
      systemReportsData
        ?.filter((r) => reportIds.includes(r.id))
        .map(({ id, reportName: name }) => ({ id, name })) || [],
    userReports:
      userReportsData
        ?.filter((r) => reportIds.includes(r.id))
        .map(({ id, reportName: name }) => ({ id, name })) || [],
    userAccess:
      resourcesData?.length > 0
        ? resourcesData
            ?.filter((opt) => {
              const codeValue = regionResourceData?.filters
                ?.find((f) => f?.filterName === "resource_type")
                ?.filterData?.find(
                  (f) => opt?.resourceType === f?.label
                )?.codeValue;
              return (
                resourceTypeIds.includes(Number(codeValue)) &&
                reconAccountIds.some((id) =>
                  opt?.resourceData?.some((f) => f.id === id)
                )
              );
            })
            .map((opt) => ({
              ...opt,
              resourceCode: regionResourceData?.filters
                ?.find((f) => f?.filterName === "resource_type")
                ?.filterData?.find((f) => opt?.resourceType === f?.label)
                ?.codeValue,
              resourceData:
                opt?.resourceData?.filter((f) =>
                  reconAccountIds.includes(f.id)
                ) || [],
            })) || []
        : [],
  };
};
